/* ─── Post date normalization ───
   Feed items carry their time in a few shapes depending on the source:
     - `published_at`: the source's own publish time (not always present)
     - `date`:      "MM-DD-YYYY" day string
     - `timestamp`: when we ingested it, unix seconds
   Any of them may arrive as unix seconds, unix millis, a numeric string, or a
   date string (ISO, or "YYYY-MM-DD HH:mm:ss" with a space, which Safari won't
   parse on its own). Everything funnels through toDate so callers never care. */

const UNIX_MILLIS_THRESHOLD = 10_000_000_000; // ~ year 2286 in seconds

const fromEpoch = (value) => {
  const millis = Math.abs(value) < UNIX_MILLIS_THRESHOLD ? value * 1000 : value;
  const parsed = new Date(millis);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? fromEpoch(value) : null;
  }

  if (typeof value !== "string") return null;

  const raw = value.trim();
  if (!raw) return null;

  // Unix seconds/millis that came through as a string ("1727999999")
  if (/^\d+(\.\d+)?$/.test(raw)) return fromEpoch(Number(raw));

  // "MM-DD-YYYY" day string: local midnight
  const mmddyyyy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mmddyyyy) {
    const [, mm, dd, yyyy] = mmddyyyy;
    const parsed = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // "YYYY-MM-DD HH:mm:ss" (Python str(datetime)): make it ISO-shaped
  const spaced = raw.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(.*)$/);
  const normalized = spaced ? `${spaced[1]}T${spaced[2]}${spaced[3]}` : raw;

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// published_at wins when the source gives us one; otherwise fall back to what
// the item had before published_at existed.
export const getPostDate = (item) =>
  toDate(item?.published_at) || toDate(item?.date) || toDate(item?.timestamp);

export const getPostMillis = (item) => getPostDate(item)?.getTime() ?? 0;

export const getPostSeconds = (item) => Math.floor(getPostMillis(item) / 1000);

// "10-05-2026" in the viewer's local time; "" when the item has no date
export const formatPostDate = (item) => {
  const date = getPostDate(item);
  if (!date) return "";

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}-${dd}-${date.getFullYear()}`;
};
